import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { StudentReportEditProps } from '../teacher/StudentReportEdit';
import ScheduledLessonReport, {
  GradeEnum,
} from '@/models/ScheduledLessonReport';
import { StudentRatingReport } from './StudentRatingReport';
import { AccordionTextarea } from '@/components/ui/custom/dataInput/AccordionTextarea';
import {
  AssignmentType,
  HomeworkActivityProgress,
} from '@/models/HomeworkActivity';
import SaveCancelGroup from '@/components/ui/custom/buttons/SaveCancelGroup';
import useStudentReport from '@/data/hook/useStudentReport';
import { updateMgActivityProgress } from '@/data/services/mgActivityProgressServices';
import { Reward } from '@/models/Rewards';
import { buildChangedObject } from '@/utils/buildChangedObject';
import alert from '@/utils/shared/alert';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { SheetTab, SheetTabs } from '@/models/SheetTab';
import ToggleButtonGroup, {
  ToggleButton,
} from '@/components/ui/custom/dataInput/ToggleGroup';
import { useTranslation } from 'react-i18next';
import { OccurrenceAssessmentFields } from '../occurrent/OccurrenceAssessmentForm';
import {
  pedagogicalSheetsQueryKeys,
  studentAssessmentsQuerykeys,
} from '@/data/services/querykeys';
import { REQUEST_STALE_TIME_IN_MS as staleTime } from '@/constants';
import { useQuery } from '@tanstack/react-query';
import { OccurrenceAssessment } from '@/models/StudentAssessment';

import {
  createStudentAssessment,
  updateStudentAssessment,
} from '@/data/services/studentAssessmentsServices';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Skeleton from '@/components/ui/custom/Skeleton';
import useManualQuery from '@/data/hook/query/v1/useManualQuery';

type PickProps = Pick<
  StudentReportEditProps,
  'enrollment' | 'canEditCash' | 'scheduledLesson'
>;

type StudentReportFormProps = Required<PickProps> & {
  homeworkProgress?: HomeworkActivityProgress;
  report?: ScheduledLessonReport;
  invalidateReports?: () => Promise<void>;
  invalidateHomeworks?: () => Promise<void>;
  reward: Reward;
};

export type ReportFields = Partial<ScheduledLessonReport> & {
  notDone: boolean;
  homeworkGrade: GradeEnum | null;
  allValues?: GradeEnum | null;
  presenceReward: number;
  occurrenceAssessment?: Pick<
    OccurrenceAssessment,
    'description' | 'notificable'
  >;
};

export function StudentReportForm({
  scheduledLesson,
  reward,
  enrollment,
  canEditCash,
  homeworkProgress,
  report,
  invalidateReports,
  invalidateHomeworks,
}: StudentReportFormProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pedagogicalSheet',
  });

  const [changed, setChanged] = useState(false);

  const [tab, setTab] = useState<SheetTab>('comment');

  const { update, create } = useStudentReport();

  const { data: pedagogicalSheetResponse, isInitialLoading: isLoadingSheet } =
    useQuery({
      ...pedagogicalSheetsQueryKeys.list({ studentId: enrollment.student.id }),
      enabled: !!enrollment.student.id && tab === 'occurrence',
      staleTime,
    });

  const [pedagogicalSheet] = pedagogicalSheetResponse?.results ?? [];

  const assessmentFilter = {
    studentId: enrollment.student.id,
    scheduledLessonId: [scheduledLesson.id],
  };

  const { data: studentAssessments, isInitialLoading: isLoadingOccurrences } =
    useQuery({
      ...studentAssessmentsQuerykeys.list(assessmentFilter),
      enabled:
        !!enrollment.student.id && !!scheduledLesson.id && tab === 'occurrence',
      staleTime,
    });

  const studentAssessment = studentAssessments?.results.at(0);

  const loadingPedagogicalAndOccurrence =
    isLoadingSheet || isLoadingOccurrences;

  const { addItem, updateItem } = useManualQuery(
    studentAssessmentsQuerykeys.list(assessmentFilter),
  );

  const checkHomework = () => {
    const hasAssignment =
      homeworkProgress?.homework.assignmentType === AssignmentType.TEXT;

    if (hasAssignment)
      return (
        !!homeworkProgress.answer || !!homeworkProgress.activityProgress.grade
      );

    return homeworkProgress?.activityProgress.hasDone ?? false;
  };

  const isHomeworkDone = checkHomework();

  const defaultValues = useMemo<ReportFields>(
    () => ({
      ...report,
      presenceReward: report?.presenceReward ?? reward.amount,
      homeworkGrade: homeworkProgress?.activityProgress.grade || null,
      notDone: !isHomeworkDone,
      notes: report?.notes,
      allValues: null,
      occurrenceAssessment: studentAssessment?.occurrenceAssessment,
    }),
    [report, reward, homeworkProgress, isHomeworkDone, studentAssessment],
  );

  const { control, handleSubmit, register, reset, watch, setValue } =
    useForm<ReportFields>({
      defaultValues,
    });

  useEffect(() => {
    reset(defaultValues);
    setChanged(false);
  }, [defaultValues, reset]);

  useEffect(() => {
    const subscription = watch(() => setChanged(true));
    return () => subscription.unsubscribe();
  }, [watch, setChanged]);

  const resetFields = () => {
    reset(defaultValues);
    setChanged(false);
  };

  const onSubmitAssessment = async (
    changes: Pick<ReportFields, 'occurrenceAssessment'>,
  ) => {
    if (!changes?.occurrenceAssessment?.description) return;

    if (!studentAssessment) {
      const assessment = await createStudentAssessment({
        pedagogicalSheetId: pedagogicalSheet.id,
        occurrenceAssessment: {
          ...changes?.occurrenceAssessment,
          scheduledLessonId: scheduledLesson.id,
        },
        klassId: scheduledLesson.klass,
      });
      addItem(assessment);
    } else {
      if (changes?.occurrenceAssessment) {
        const changedFields = buildChangedObject(
          studentAssessment.occurrenceAssessment,
          changes.occurrenceAssessment,
        );

        const updatedAssessment = await updateStudentAssessment({
          assessmentId: studentAssessment.id,
          body: { occurrenceAssessment: changedFields },
        });
        updateItem(updatedAssessment);
      }
    }
  };

  const onSubmitHomework = async (
    changes: Partial<Pick<ReportFields, 'homeworkGrade' | 'notDone'>>,
  ) => {
    if (homeworkProgress) {
      const { homeworkGrade, notDone } = changes;

      const hasDone = notDone || homeworkGrade ? !notDone : undefined;

      const mgActivityProgressId = homeworkProgress.activityProgress.id;

      const updated = await updateMgActivityProgress(mgActivityProgressId, {
        grade: notDone ? 0 : homeworkGrade,
        hasDone,
      });

      if (updated.id) await invalidateHomeworks?.();
    }
  };

  const homeworkChanged = (changes: Partial<ReportFields>) =>
    'homeworkGrade' in changes || 'notDone' in changes;

  const occurrenceChanged = (changes: Partial<ReportFields>) =>
    'occurrenceAssessment' in changes;

  const onUpdate = async (id: number, changes: Partial<ReportFields>) => {
    if (!defaultValues) return;

    if (Object.keys(changes).length) {
      const updated = await update({
        id,
        changes,
      });

      if (updated?.id) await invalidateReports?.();
    }
  };

  const onCreate = async (
    changes: Partial<ReportFields> & {
      scheduledLesson: number;
      student: number;
    },
  ) => {
    const { homeworkGrade, notDone, ...rest } = changes;

    const created = await create(rest);

    if (created?.id) await invalidateReports?.();
  };

  const [loading, setLoading] = useState(false);

  const setReaward = (changes: Partial<ReportFields>) => {
    if (changes.presenceReward) return changes.presenceReward;

    return !report?.presenceReward ? reward.amount : undefined;
  };

  const onSubmit = async (changes: ReportFields) => {
    try {
      setLoading(true);

      const changedFields = buildChangedObject<ReportFields>(
        defaultValues,
        changes,
      );

      const { homeworkGrade, notDone, occurrenceAssessment } = changedFields;

      if (homeworkChanged(changedFields))
        await onSubmitHomework({ homeworkGrade, notDone });

      if (occurrenceChanged(changedFields))
        await onSubmitAssessment({ occurrenceAssessment });

      await handleSubmitReport(changedFields);
    } catch (error) {
      alert.error(getErrorMessage(error));
    } finally {
      setChanged(false);
      setLoading(false);
    }
  };

  async function handleSubmitReport(changes: Partial<ReportFields>) {
    const { homeworkGrade, notDone, occurrenceAssessment, ...rest } = changes;

    const { allValues, ...allValuesRemoved } = rest;

    if (Object.keys(allValuesRemoved).length) {
      if (defaultValues?.id)
        await onUpdate(defaultValues.id, {
          ...rest,
          presenceReward: setReaward(rest),
        });
      else {
        await onCreate({
          ...rest,
          scheduledLesson: scheduledLesson.id,
          student: enrollment.student.id,
          presenceReward: rest.presenceReward ?? reward.amount,
        });
      }
    }
  }

  const buttons: ToggleButton<Partial<SheetTab>>[] = [
    {
      testId: 'addCommentButton',
      text: t('comment'),
      value: 'comment',
    },
    {
      testId: 'addOccurrenceButton',
      text: t('occurrence'),
      value: 'occurrence',
    },
  ];

  const tabs: Partial<SheetTabs> = {
    comment: (
      <AccordionTextarea
        register={register('notes')}
        testId="accordionTextarea"
        studentName={enrollment.student.firstName}
      />
    ),
    occurrence: (
      <ConditionalRenderer
        condition={!loadingPedagogicalAndOccurrence}
        fallback={
          <Skeleton className="bg-primary-content rounded-xl w-full h-30" />
        }
      >
        <OccurrenceAssessmentFields
          control={control}
          descriptionName="occurrenceAssessment.description"
          notificableName="occurrenceAssessment.notificable"
          idName="occurrenceAssessment.id"
        />
      </ConditionalRenderer>
    ),
  };

  return (
    <form
      data-testid="studentReportEdit"
      className="flex flex-col gap-y-4"
      onSubmit={handleSubmit(onSubmit)}
    >
      <StudentRatingReport
        control={control}
        homeworkProgress={homeworkProgress}
        rewardAmount={reward.amount}
        locked={!canEditCash}
        setValue={setValue}
      />

      <ToggleButtonGroup
        buttons={buttons}
        value={tab}
        onChange={setTab}
        exclusive
      />

      {tabs[tab]}

      <SaveCancelGroup
        loading={loading}
        cancel={{
          onClick: resetFields,
          testId: 'cancelButton',
        }}
        save={{
          disabled: !changed || loadingPedagogicalAndOccurrence,
          testId: 'saveButton',
        }}
      />
    </form>
  );
}
